import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { m } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Footer from '../Footer/Footer';

import useNewsPosts from 'hooks/useNewsPosts';
import { formatDate } from 'utils/date';

import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/opacity.css';

import RichTextContent from 'components/RichTextContent/RichTextContent';
import { TeamMember } from 'types/TeamMember';

import './SingleNews.scss';
import { routes } from '../../routing/routes';

const getSecondaryHeader = (publishDate: Date, authors?: TeamMember[]) => {
  let secondaryHeader = `Published ${formatDate(publishDate)}`;

  if (authors) {
    secondaryHeader += ` by ${authors.map(({ name }) => name).join(', ')}`;
  }

  return secondaryHeader;
};

export default function SingleNews() {
  const { id } = useParams();
  const { isLoading, newsPost } = useNewsPosts(id);

  if (isLoading || !newsPost) {
    return null;
  }

  const { title, blurb, imageUrl, publishDate, content, authors } = newsPost;

  return (
    <m.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="media"
    >
      <Helmet>
        <title>{`${title} - DuskBreakers`}</title>
        <meta name="description" content={blurb} />
      </Helmet>

      <section>
        <div className="container">
          <div className="row d-flex justify-content-center">
            <div className="col">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.news.base()}>News</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {title}
                  </li>
                </ol>
              </nav>
              <h1 className="mb-3">
                <span>{title}</span>
              </h1>
              <p className="text-muted mb-5">{getSecondaryHeader(publishDate, authors)}</p>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="row d-flex justify-content-center">
            <div className="col">
              <LazyLoadImage
                className="img-fluid rounded mb-5"
                alt={title}
                src={imageUrl}
                effect="opacity"
              />
            </div>
          </div>
        </div>

        <div className="container news-post-container">
          <div className="row d-flex justify-content-center">
            <div className="col-md-8">
              <div className="post">
                <div className="row d-flex justify-content-center">
                  <div className="col">
                    <div className="post__body" id="loreid">
                      <RichTextContent node={content} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </m.main>
  );
}
